import { ColumnHeader, LeaderboardKpi, SaleType } from '@models/Products';
import { Kpi } from '@utils/Formatters';
import { FocusArea } from '../Types';

export type MarketShareProperty = `${ColumnHeader.marketShareRevenue}` | `${ColumnHeader.marketShareVolume}`;

export type RetailerShareProperty = `${ColumnHeader.retailerShareRevenue}` | `${ColumnHeader.retailerShareVolume}`;

export type BothShareProperty = MarketShareProperty | RetailerShareProperty;

const getMarketShareProperty = (selectedSaleType: SaleType, selectedFocusArea: FocusArea): BothShareProperty => {
  let property: BothShareProperty = Kpi.marketShareRevenue;
  if (selectedFocusArea === FocusArea.market) {
    if (selectedSaleType === SaleType.volume) {
      property = Kpi.marketShareVolume;
    }
  } else if (selectedSaleType === SaleType.revenue) {
    property = Kpi.retailerShareRevenue;
  } else {
    property = Kpi.retailerShareVolume;
  }
  return property;
};

/**
 * To get the market share (revenue / volume) ratio based on the following formula:
 *
 * Current market share / Highest market share * 100
 *
 * @param kpis all KPIs, used to retrieve the highest market share
 * @param currentKpi current KPI
 * @returns the calculated market share ratio
 */
export const getMarketShareRatio = (
  kpis: LeaderboardKpi[],
  currentKpi: LeaderboardKpi,
  marketShareProperty: BothShareProperty,
): number => {
  const highest = Math.max(...kpis.map((kpi) => kpi[marketShareProperty].sharePercentage));
  return highest === 0 ? 0 : Math.ceil((currentKpi[marketShareProperty].sharePercentage / highest) * 100);
};

/**
 * To get the KPI object that contains the calculated market share ratio
 * and market share value
 *
 * @param kpis all KPIs, used to retrieve the highest market share
 * @param currentKpi current KPI
 * @param selectedSaleType sale type, determines whether revenue or volume
 * ratio and values are stored in the returned object
 * @returns object with calculated market share ratio and market share value
 */
export const getKpiWithMarketShare = (
  kpis: LeaderboardKpi[],
  currentKpi: LeaderboardKpi,
  selectedSaleType: SaleType,
  selectedFocusArea: FocusArea,
): LeaderboardKpi => {
  const property = getMarketShareProperty(selectedSaleType, selectedFocusArea);
  const ratio = getMarketShareRatio(kpis, currentKpi, property);
  const value = currentKpi[property].sharePercentage;
  return { ...currentKpi, marketShareRatio: ratio, marketShareValue: value };
};

export const getColorByIndex = (colorIndex: number | undefined): string => {
  return `colors-${(colorIndex ?? 0) % 30}`;
};
